import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  Paper,
  Card,
  CardContent,
  Avatar,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Savings,
  Favorite,
  Store,
  AccessTime,
  Apple,
  ShoppingBag,
  LocalOffer,
  LocationOn,
  Star,
  TrendingDown,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DotGridBackground, ThreadsBackground, Particles } from '../ui';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [vendors, setVendors] = useState<any[]>([]);
  const [vendorScrollX, setVendorScrollX] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [categories, setCategories] = useState<any[]>([]);
  const [visibleCategories, setVisibleCategories] = useState<Set<number>>(new Set());

  useEffect(() => {
    // Fetch vendors - using public API endpoint if available
    const fetchVendors = async () => {
      try {
        // Use featured stores endpoint (no location required)
        const response = await fetch('https://godiscountdays.com/api/v1/stores/featured?limit=10');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data?.stores) {
            setVendors(data.data.stores.slice(0, 8));
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
      
      // Fallback to mock data if API fails
      setVendors([
        { id: 1, businessName: 'Fashion Hub', category: 'Fashion', rating: { average: 4.5, count: 120 } },
        { id: 2, businessName: 'Tech World', category: 'Electronics', rating: { average: 4.8, count: 89 } },
        { id: 3, businessName: 'Food Delights', category: 'Food', rating: { average: 4.3, count: 156 } },
        { id: 4, businessName: 'Beauty Studio', category: 'Health & Beauty', rating: { average: 4.7, count: 94 } },
        { id: 5, businessName: 'Sports Zone', category: 'Sports & Fitness', rating: { average: 4.6, count: 78 } },
        { id: 6, businessName: 'Home Essentials', category: 'Home & Garden', rating: { average: 4.4, count: 112 } },
        { id: 7, businessName: 'Jewelry Palace', category: 'Jewelry & Accessories', rating: { average: 4.9, count: 65 } },
        { id: 8, businessName: 'Wellness Center', category: 'Wellness & Spa', rating: { average: 4.5, count: 87 } },
      ]);
    };

    fetchVendors();
  }, []);

  useEffect(() => {
    // Fetch categories
    const fetchCategories = async () => {
      try {
        const response = await fetch('https://godiscountdays.com/api/v1/categories');
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setCategories(data.data);
            // Animate categories in sequence
            data.data.forEach((_: any, index: number) => {
              setTimeout(() => {
                setVisibleCategories(prev => {
                  const newSet = new Set(prev);
                  newSet.add(index);
                  return newSet;
                });
              }, index * 100);
            });
            return;
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
      
      // Fallback categories
      const fallbackCategories = [
        { _id: 'food-dining', name: 'Food & Dining', icon: '🍽️' },
        { _id: 'fashion-style', name: 'Fashion & Style', icon: '👕' },
        { _id: 'electronics', name: 'Electronics', icon: '📱' },
        { _id: 'health-beauty', name: 'Health & Beauty', icon: '💄' },
        { _id: 'home-garden', name: 'Home & Garden', icon: '🏠' },
        { _id: 'sports-fitness', name: 'Sports & Fitness', icon: '⚽' },
        { _id: 'automotive', name: 'Automotive', icon: '🚗' },
        { _id: 'travel-hotels', name: 'Travel & Hotels', icon: '✈️' },
        { _id: 'entertainment', name: 'Entertainment', icon: '🎬' },
        { _id: 'services', name: 'Services', icon: '🔧' },
        { _id: 'books-media', name: 'Books & Media', icon: '📚' },
        { _id: 'pets-animals', name: 'Pets & Animals', icon: '🐕' },
      ];
      setCategories(fallbackCategories);
      fallbackCategories.forEach((_, index) => {
        setTimeout(() => {
          setVisibleCategories(prev => {
            const newSet = new Set(prev);
            newSet.add(index);
            return newSet;
          });
        }, index * 100);
      });
    };

    fetchCategories();
  }, []);

  const scrollVendors = (direction: 'left' | 'right') => {
    const cardWidth = isMobile ? 280 : 300;
    const gap = 16;
    const scrollAmount = cardWidth + gap;
    const visibleCards = isMobile ? 1 : 3;
    const maxScroll = Math.max(0, (vendors.length - visibleCards) * scrollAmount);
    
    if (direction === 'left') {
      setVendorScrollX(Math.max(0, vendorScrollX - scrollAmount));
    } else {
      setVendorScrollX(Math.min(maxScroll, vendorScrollX + scrollAmount));
    }
  };

  // Auto-slide carousel with pause on hover
  useEffect(() => {
    if (vendors.length === 0 || !isAutoPlaying) return;
    
    const cardWidth = isMobile ? 280 : 300;
    const gap = 16;
    const scrollAmount = cardWidth + gap;
    const visibleCards = isMobile ? 1 : 3;
    const maxScroll = Math.max(0, (vendors.length - visibleCards) * scrollAmount);
    
    const interval = setInterval(() => {
      setVendorScrollX((prev) => {
        const next = prev + scrollAmount;
        if (next >= maxScroll) {
          return 0; // Reset to start with smooth transition
        }
        return next;
      });
    }, 4000); // Auto-slide every 4 seconds

    return () => clearInterval(interval);
  }, [vendors.length, isMobile, isAutoPlaying]);

  const benefits = [
    {
      icon: <Savings sx={{ fontSize: 32, color: '#0084FF' }} />,
      title: 'Save Money',
      description: 'Discover amazing deals and discounts across Food, Fashion, Electronics, Health & Beauty, and more categories.',
      align: 'left',
    },
    {
      icon: <Favorite sx={{ fontSize: 32, color: '#0084FF' }} />,
      title: 'Find Hot Deals',
      description: 'Get access to exclusive deals and limited-time offers across all categories from top stores and businesses.',
      align: 'right',
    },
    {
      icon: <Store sx={{ fontSize: 32, color: '#0084FF' }} />,
      title: 'Support Local',
      description: 'Connect with and support your neighborhood businesses across all categories, helping local stores thrive while getting great deals.',
      align: 'left',
    },
    {
      icon: <AccessTime sx={{ fontSize: 32, color: '#0084FF' }} />,
      title: 'Exclusive Offers',
      description: 'Access special promotions and exclusive discounts available only through DiscountDays.',
      align: 'right',
    },
  ];

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* Header */}
          <AppBar 
        position="sticky" 
            elevation={0} 
            sx={{ 
          backgroundColor: 'white',
          color: '#050206',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <Toolbar
              sx={{
            px: { xs: 2, sm: 4, md: 6 },
            py: 1.5,
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: { xs: 1, md: 0 } }}>
            <Box
              component="img"
              src="/logo.png"
              alt="DiscountDays Logo"
              sx={{
                width: 40,
                height: 40,
                objectFit: 'contain',
                mr: 1.5,
              }}
            />
              <Typography 
              variant="h6" 
                component="div" 
                sx={{ 
                fontWeight: 700,
                color: '#3B3B3B',
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                }}
              >
                DiscountDays
              </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', gap: 3, mx: 4 }}>
                <Button 
                  onClick={() => navigate('/features')} 
                  sx={{ 
                    color: '#050206',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 59, 59, 0.05)',
                    },
                  }}
                >
                  Features
                </Button>
                <Button 
                  onClick={() => navigate('/about')} 
                  sx={{ 
                    color: '#050206',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 59, 59, 0.05)',
                    },
                  }}
                >
                  About Us
                </Button>
                <Button 
                  onClick={() => navigate('/contact')} 
                  sx={{ 
                    color: '#050206',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 59, 59, 0.05)',
                    },
                  }}
                >
                  Contact
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                variant="contained" 
                sx={{ 
                    backgroundColor: '#0084FF',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2,
                  '&:hover': {
                      backgroundColor: '#0066CC',
                  },
                }}
                onClick={() => navigate('/login')}
              >
                Admin Login
                </Button>
              </Box>
            </>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setMobileOpen(true)}
              sx={{ ml: 'auto' }}
            >
              <MenuIcon />
            </IconButton>
          )}
            </Toolbar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{
            sx: {
              width: 280,
              backgroundColor: 'white',
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#3B3B3B' }}>
              Menu
              </Typography>
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/features'); setMobileOpen(false); }}>
                  <ListItemText primary="Features" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/about'); setMobileOpen(false); }}>
                  <ListItemText primary="About Us" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/contact'); setMobileOpen(false); }}>
                  <ListItemText primary="Contact" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding sx={{ mt: 2 }}>
                <Button
                  fullWidth
                variant="contained"
                  sx={{
                    backgroundColor: '#0084FF',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  onClick={() => { navigate('/login'); setMobileOpen(false); }}
                >
                  Admin Login
                </Button>
              </ListItem>
            </List>
          </Box>
        </Drawer>
          </AppBar>

      {/* Hero Section 1 - "Let's get started finding deals around the world!" */}
      <ThreadsBackground
        threadCount={8}
        speed={1.0}
        thickness={0.008}
        color="#0084FF"
        backgroundColor="white"
        mouseInteraction={true}
        transparent={true}
        sx={{
          py: { xs: 6, md: 12 },
          px: { xs: 2, sm: 4, md: 6 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: { xs: 'center', lg: 'flex-start' },
              gap: { xs: 4, md: 6 },
            }}
          >
            {/* Left Content */}
            <Box
              sx={{
                flex: { xs: '1 1 100%', lg: '1 1 50%' },
                textAlign: { xs: 'center', lg: 'left' },
              }}
            >
              <Typography
                variant={isMobile ? 'h3' : 'h1'}
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: '#050206',
                  lineHeight: 1.2,
                  mb: 3,
                  fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
                  animation: 'fadeInUp 0.8s ease-out',
                  '@keyframes fadeInUp': {
                    '0%': { opacity: 0, transform: 'translateY(40px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' },
                  },
                }}
              >
                Let's get started{' '}
                <Box
                  component="span"
                  sx={{
                    backgroundColor: 'rgba(0, 132, 255, 0.15)',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    display: 'inline-block',
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { transform: 'scale(1)', backgroundColor: 'rgba(0, 132, 255, 0.15)' },
                      '50%': { transform: 'scale(1.05)', backgroundColor: 'rgba(0, 132, 255, 0.25)' },
                    },
                  }}
                >
                  finding deals
        </Box>
                {' '}around the world!
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: '#050206',
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  lineHeight: 1.7,
                  mb: 4,
                  maxWidth: { xs: '100%', md: '90%' },
                  animation: 'fadeInUp 0.8s ease-out 0.2s both',
                }}
              >
                DiscountDays connects you to amazing deals across Food, Fashion, Electronics, Health & Beauty, and more. Discover{' '}
                <Box
                  component="span"
          sx={{
                    textDecoration: 'underline',
                    textDecorationColor: '#0084FF',
                    textUnderlineOffset: 4,
                    fontWeight: 600,
                  }}
                >
                  hot deals
                </Box>
                , explore different categories, and save money on everything you love while supporting local businesses.
              </Typography>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: { xs: 'center', lg: 'flex-start' },
                  animation: 'fadeInUp 0.8s ease-out 0.4s both',
                }}
              >
                <Button
                  component="a"
                  href="https://apps.apple.com/in/app/discount-days-app/id6756425497"
                  target="_blank"
                  rel="noopener noreferrer"
                variant="contained"
                  startIcon={<Apple />}
                sx={{
                    backgroundColor: '#050206',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                  py: 1.5,
                    borderRadius: 2,
                    textDecoration: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                      backgroundColor: '#3B3B3B',
                      transform: 'translateY(-3px) scale(1.05)',
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
                  },
                }}
              >
                  App Store
                </Button>
                <Button
                  component="a"
                  href="https://play.google.com/store/apps/details?id=com.hotdeals.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  sx={{
                    backgroundColor: '#0084FF',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textDecoration: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#0066CC',
                      transform: 'translateY(-3px) scale(1.05)',
                      boxShadow: '0 8px 20px rgba(0, 132, 255, 0.4)',
                    },
                  }}
                >
                  Google Play
                </Button>
                <Button
                  component="a"
                  href="https://play.google.com/store/apps/details?id=com.discountdays.vendorapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  startIcon={<Store />}
                  sx={{
                    borderColor: '#0084FF',
                    color: '#050206',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textDecoration: 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      borderColor: '#0066CC',
                      backgroundColor: 'rgba(0, 132, 255, 0.1)',
                      transform: 'translateY(-3px) scale(1.05)',
                      boxShadow: '0 8px 20px rgba(0, 132, 255, 0.2)',
                    },
                  }}
                >
                  Register your business
                </Button>
          </Box>
        </Box>

            {/* Right - Phone Mockup with App Preview */}
            <Box
              sx={{
                flex: { xs: '1 1 100%', lg: '1 1 50%' },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                animation: 'fadeInRight 1s ease-out 0.3s both',
                '@keyframes fadeInRight': {
                  '0%': { opacity: 0, transform: 'translateX(60px)' },
                  '100%': { opacity: 1, transform: 'translateX(0)' },
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: '280px', sm: '320px', md: '360px' },
                  height: { xs: '560px', sm: '640px', md: '720px' },
                  backgroundColor: '#050206',
                  borderRadius: 4,
                  border: '8px solid #3B3B3B',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  animation: 'phoneFloat 3s ease-in-out infinite',
                  '@keyframes phoneFloat': {
                    '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                    '50%': { transform: 'translateY(-10px) rotate(1deg)' },
                  },
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: '0 30px 80px rgba(0, 132, 255, 0.3)',
                  },
                }}
              >
                {/* App Header */}
                <Box
                  sx={{
                    backgroundColor: '#3B3B3B',
                    py: 2,
                    px: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Store sx={{ color: '#0084FF', fontSize: 24 }} />
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, flexGrow: 1 }}>
                    DiscountDays
            </Typography>
                  <LocationOn sx={{ color: '#0084FF', fontSize: 20 }} />
                </Box>

                {/* App Content */}
                <Box sx={{ flex: 1, backgroundColor: '#1a1a1a', overflow: 'auto', p: 2 }}>
                  {/* Search Bar */}
                  <Box
                    sx={{
                      backgroundColor: '#3B3B3B',
                      borderRadius: 2,
                      p: 1.5,
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      animation: 'slideDown 0.6s ease-out 0.8s both',
                      '@keyframes slideDown': {
                        '0%': { opacity: 0, transform: 'translateY(-20px)' },
                        '100%': { opacity: 1, transform: 'translateY(0)' },
                      },
                    }}
                  >
                    <Store sx={{ color: '#0084FF', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', flexGrow: 1 }}>
                      Search deals...
              </Typography>
                  </Box>

                  {/* Categories */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, overflowX: 'auto' }}>
                    {['Food', 'Fashion', 'Tech'].map((cat, i) => (
                      <Box
                        key={i}
                sx={{
                          backgroundColor: i === 0 ? '#0084FF' : '#3B3B3B',
                          borderRadius: 2,
                          px: 2,
                          py: 0.8,
                          whiteSpace: 'nowrap',
                          animation: `slideInLeft 0.5s ease-out ${0.9 + i * 0.1}s both`,
                          '@keyframes slideInLeft': {
                            '0%': { opacity: 0, transform: 'translateX(-30px) scale(0.8)' },
                            '100%': { opacity: 1, transform: 'translateX(0) scale(1)' },
                          },
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.1) translateY(-2px)',
                            backgroundColor: i === 0 ? '#0099FF' : '#0084FF',
                          },
                        }}
                      >
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                          {cat}
                </Typography>
                      </Box>
                    ))}
          </Box>

                  {/* Deal Cards */}
                  {[
                    { title: 'Fashion Sale', discount: '50% OFF', icon: <ShoppingBag /> },
                    { title: 'Electronics', discount: '30% OFF', icon: <TrendingDown /> },
                    { title: 'Food Deal', discount: '25% OFF', icon: <LocalOffer /> },
                  ].map((deal, i) => (
                    <Box
                      key={i}
                      sx={{
                        backgroundColor: '#3B3B3B',
                        borderRadius: 2,
                        p: 2,
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        animation: `cardSlideUp 0.6s ease-out ${1.2 + i * 0.15}s both`,
                        '@keyframes cardSlideUp': {
                          '0%': { opacity: 0, transform: 'translateY(30px) scale(0.9)' },
                          '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
                        },
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateX(5px) scale(1.02)',
                          backgroundColor: '#4B4B4B',
                          boxShadow: '0 4px 12px rgba(0, 132, 255, 0.3)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: '#0084FF',
                          borderRadius: 1.5,
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.3s ease',
                          animation: `iconBounce 0.5s ease-out ${1.3 + i * 0.15}s both`,
                          '@keyframes iconBounce': {
                            '0%': { transform: 'scale(0) rotate(-180deg)', opacity: 0 },
                            '50%': { transform: 'scale(1.2) rotate(10deg)' },
                            '100%': { transform: 'scale(1) rotate(0deg)', opacity: 1 },
                          },
                          '&:hover': {
                            transform: 'scale(1.1) rotate(5deg)',
                            boxShadow: '0 4px 12px rgba(0, 132, 255, 0.5)',
                          },
                        }}
                      >
                        {deal.icon}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                          {deal.title}
                  </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Star sx={{ color: '#0084FF', fontSize: 14 }} />
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            4.5 • 2.5 km
                  </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#0084FF',
                          fontWeight: 700,
                          fontSize: '0.9rem',
                        }}
                      >
                        {deal.discount}
                  </Typography>
                    </Box>
                  ))}
          </Box>

                {/* Bottom Navigation */}
                <Box
              sx={{
                    backgroundColor: '#3B3B3B',
                py: 1.5,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'space-around',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <Store
                    sx={{
                      color: '#0084FF',
                      fontSize: 22,
                    }}
                  />
                  <Favorite
                    sx={{
                      color: 'rgba(255,255,255,0.4)',
                      fontSize: 22,
                    }}
                  />
                  <LocationOn
                    sx={{
                      color: 'rgba(255,255,255,0.4)',
                      fontSize: 22,
                    }}
                  />
          </Box>
      </Box>
            </Box>
        </Box>
        </Container>
      </ThreadsBackground>

      {/* Categories Section - Animated */}
      <Box
        sx={{
          backgroundColor: 'white',
          position: 'relative',
          py: { xs: 8, md: 12 },
          px: { xs: 2, sm: 4 },
          overflow: 'hidden',
        }}
      >
        {/* Animated Background */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0, 132, 255, 0.02) 0%, rgba(0, 180, 255, 0.02) 100%)',
            opacity: 0.5,
            animation: 'pulse 8s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 0.3 },
              '50%': { opacity: 0.6 },
            },
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant={isMobile ? 'h3' : 'h2'}
              component="h2"
              sx={{
                fontWeight: 700,
                color: '#050206',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
                animation: 'fadeInUp 0.8s ease-out',
                '@keyframes fadeInUp': {
                  '0%': { opacity: 0, transform: 'translateY(30px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              Explore All Categories
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#0084FF',
                fontSize: { xs: '0.95rem', md: '1.1rem' },
                maxWidth: '600px',
                mx: 'auto',
                animation: 'fadeInUp 0.8s ease-out 0.2s both',
              }}
            >
              Discover amazing deals across {categories.length || 12}+ categories
            </Typography>
          </Box>

          {/* Categories Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(4, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: { xs: 2, md: 3 },
            }}
          >
            {(categories.length > 0 ? categories : [
              { _id: 'food-dining', name: 'Food & Dining', icon: '🍽️' },
              { _id: 'fashion-style', name: 'Fashion & Style', icon: '👕' },
              { _id: 'electronics', name: 'Electronics', icon: '📱' },
              { _id: 'health-beauty', name: 'Health & Beauty', icon: '💄' },
              { _id: 'home-garden', name: 'Home & Garden', icon: '🏠' },
              { _id: 'sports-fitness', name: 'Sports & Fitness', icon: '⚽' },
              { _id: 'automotive', name: 'Automotive', icon: '🚗' },
              { _id: 'travel-hotels', name: 'Travel & Hotels', icon: '✈️' },
              { _id: 'entertainment', name: 'Entertainment', icon: '🎬' },
              { _id: 'services', name: 'Services', icon: '🔧' },
              { _id: 'books-media', name: 'Books & Media', icon: '📚' },
              { _id: 'pets-animals', name: 'Pets & Animals', icon: '🐕' },
            ]).slice(0, 12).map((category: any, index: number) => {
              const isVisible = visibleCategories.has(index) || categories.length === 0;
              return (
                <Paper
                  key={category._id || index}
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, md: 3.5 },
                    borderRadius: 3,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    border: '2px solid rgba(0, 132, 255, 0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.9)',
                    opacity: isVisible ? 1 : 0,
          position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(0, 132, 255, 0.1), transparent)',
                      transition: 'left 0.5s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.05)',
                      borderColor: '#0084FF',
                      boxShadow: '0 12px 40px rgba(0, 132, 255, 0.25)',
                      '&::before': {
                        left: '100%',
                      },
                      '& .category-icon': {
                        transform: 'scale(1.2) rotate(5deg)',
                      },
                    },
                  }}
                >
                  <Box
                    className="category-icon"
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      mb: 1.5,
                      display: 'inline-block',
                      transition: 'transform 0.3s ease',
                      animation: isVisible ? 'bounceIn 0.6s ease-out' : 'none',
                      '@keyframes bounceIn': {
                        '0%': { transform: 'scale(0)', opacity: 0 },
                        '50%': { transform: 'scale(1.1)' },
                        '100%': { transform: 'scale(1)', opacity: 1 },
                      },
                    }}
                  >
                    {category.icon || '🏷️'}
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: '#050206',
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      lineHeight: 1.4,
                    }}
                  >
                    {category.name}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* Why DiscountDays Section - with Dot Grid Background */}
      <Box
        sx={{
          backgroundColor: '#fafbfc',
          position: 'relative',
          py: { xs: 8, md: 12 },
          px: { xs: 2, sm: 4 },
          overflow: 'hidden',
        }}
      >
        {/* Subtle background pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(0, 132, 255, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(0, 180, 255, 0.03) 0%, transparent 50%)',
            pointerEvents: 'none',
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant={isMobile ? 'h3' : 'h2'}
            component="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
                color: '#050206',
                mb: 2,
              fontSize: { xs: '2rem', md: '3rem' },
                animation: 'fadeInUp 0.8s ease-out',
            }}
          >
              Why DiscountDays?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                color: '#666',
                fontSize: { xs: '1rem', md: '1.125rem' },
                maxWidth: '600px',
                mx: 'auto',
                animation: 'fadeInUp 0.8s ease-out 0.2s both',
              }}
            >
              Everything you need to save money and discover amazing deals
            </Typography>
          </Box>
          
          {/* Vertical Timeline with Benefits */}
          <Box
            sx={{
              position: 'relative',
              maxWidth: { xs: '100%', md: '900px' },
              mx: 'auto',
            }}
          >
            {/* Central Line */}
            <Box
              sx={{
                position: 'absolute',
                left: { xs: '50%', md: '50%' },
                transform: 'translateX(-50%)',
                width: 2,
                height: '100%',
                backgroundColor: 'rgba(0, 132, 255, 0.3)',
                display: { xs: 'none', md: 'block' },
              }}
            />

            {benefits.map((benefit, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  mb: { xs: 6, md: 8 },
                display: 'flex',
                  flexDirection: {
                    xs: 'column',
                    md: benefit.align === 'left' ? 'row' : 'row-reverse',
                  },
                  alignItems: { xs: 'center', md: 'center' },
                  gap: { xs: 2, md: 4 },
                }}
              >
                {/* Content Box */}
                <Box
                  sx={{
                    flex: { xs: '1 1 100%', md: '1 1 45%' },
                    textAlign: { xs: 'center', md: benefit.align },
                    px: { xs: 2, md: 0 },
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: 'white',
                      border: '2px solid rgba(0, 132, 255, 0.1)',
                      borderRadius: 4,
                      p: { xs: 3.5, md: 4.5 },
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      animation: `fadeInUp 0.6s ease-out ${index * 0.15}s both`,
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: 'linear-gradient(90deg, #0084FF 0%, #00B4FF 100%)',
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.6s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-12px) scale(1.02)',
                        backgroundColor: 'white',
                        borderColor: '#0084FF',
                        boxShadow: '0 20px 60px rgba(0, 132, 255, 0.25), 0 0 0 1px rgba(0, 132, 255, 0.1)',
                        '&::before': {
                          transform: 'scaleX(1)',
                        },
                },
              }}
            >
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(0, 132, 255, 0.15) 0%, rgba(0, 180, 255, 0.15) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        mx: { xs: 'auto', md: benefit.align === 'left' ? 0 : 'auto' },
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: '2px solid rgba(0, 132, 255, 0.2)',
                        boxShadow: '0 4px 12px rgba(0, 132, 255, 0.1)',
                        '&:hover': {
                          transform: 'scale(1.15) rotate(5deg)',
                          background: 'linear-gradient(135deg, rgba(0, 132, 255, 0.25) 0%, rgba(0, 180, 255, 0.25) 100%)',
                          borderColor: '#0084FF',
                          boxShadow: '0 8px 24px rgba(0, 132, 255, 0.3)',
                        },
                      }}
                    >
                      {benefit.icon}
                  </Box>
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{
                        fontWeight: 700,
                        color: '#050206',
                        mb: 2,
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {benefit.title}
                  </Typography>
                      <Typography
                      variant="body1"
                        sx={{
                        color: '#666',
                        lineHeight: 1.8,
                        fontSize: { xs: '0.95rem', md: '1rem' },
                        }}
                      >
                      {benefit.description}
                  </Typography>
                  </Paper>
                </Box>

                {/* Diamond Marker (Desktop only) */}
                <Box
                  sx={{
                    position: { xs: 'relative', md: 'absolute' },
                    left: { xs: 'auto', md: '50%' },
                    transform: { xs: 'none', md: 'translateX(-50%)' },
                    width: 24,
                    height: 24,
                    background: 'linear-gradient(135deg, #0084FF 0%, #00B4FF 100%)',
                    borderRadius: '50%',
                    border: '5px solid white',
                    boxShadow: '0 4px 12px rgba(0, 132, 255, 0.4)',
                    zIndex: 2,
                    display: { xs: 'none', md: 'block' },
                    animation: `markerPulse 0.6s ease-out ${index * 0.15 + 0.3}s both`,
                    '@keyframes markerPulse': {
                      '0%': { opacity: 0, transform: 'translateX(-50%) scale(0)' },
                      '50%': { transform: 'translateX(-50%) scale(1.2)' },
                      '100%': { opacity: 1, transform: 'translateX(-50%) scale(1)' },
                    },
                  }}
                />
            </Box>
          ))}
        </Box>
        </Container>
      </Box>

      {/* Our Vendors Carousel Section */}
      <Box
        sx={{
          backgroundColor: 'white',
          py: { xs: 8, md: 12 },
          px: { xs: 2, sm: 4 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative Background Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '200px',
            background: 'linear-gradient(180deg, rgba(0, 132, 255, 0.03) 0%, transparent 100%)',
            pointerEvents: 'none',
          }}
        />
        
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant={isMobile ? 'h3' : 'h2'}
            component="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: '#050206',
                mb: 2,
              fontSize: { xs: '2rem', md: '3rem' },
              position: 'relative',
              zIndex: 1,
                animation: 'fadeInUp 0.8s ease-out',
            }}
          >
            Our Featured Vendors
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
                color: '#666',
              mb: { xs: 5, md: 7 },
                fontSize: { xs: '0.95rem', md: '1.125rem' },
                maxWidth: '600px',
                mx: 'auto',
              position: 'relative',
              zIndex: 1,
                animation: 'fadeInUp 0.8s ease-out 0.2s both',
            }}
          >
              Discover trusted businesses offering amazing deals and exclusive discounts
          </Typography>
          </Box>

          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, md: 3 },
            }}
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            {/* Left Arrow with Gradient */}
            <IconButton
              onClick={() => scrollVendors('left')}
              disabled={vendorScrollX === 0}
              sx={{
                display: { xs: 'none', md: 'flex' },
                width: 48,
                height: 48,
                backgroundColor: 'white',
                color: '#0084FF',
                border: '2px solid #0084FF',
                boxShadow: '0 4px 12px rgba(0, 132, 255, 0.2)',
                zIndex: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#0084FF',
                  color: 'white',
                  transform: 'scale(1.1)',
                  boxShadow: '0 6px 20px rgba(0, 132, 255, 0.4)',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(0, 132, 255, 0.1)',
                  borderColor: 'rgba(0, 132, 255, 0.3)',
                  color: 'rgba(0, 132, 255, 0.3)',
                },
              }}
            >
              ←
            </IconButton>

            {/* Carousel Container with Gradient Overlays */}
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                position: 'relative',
                borderRadius: 4,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '80px',
                  background: 'linear-gradient(to right, white, transparent)',
                  zIndex: 2,
                  pointerEvents: 'none',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: '80px',
                  background: 'linear-gradient(to left, white, transparent)',
                  zIndex: 2,
                  pointerEvents: 'none',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: 2, md: 3 },
                  transform: `translateX(-${vendorScrollX}px)`,
                  transition: 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                  willChange: 'transform',
                }}
              >
                {vendors.map((vendor, index) => {
                  const isVisible = Math.abs(vendorScrollX - (index * (isMobile ? 296 : 316))) < 1000;
                  return (
                    <Card
                      key={vendor.id || vendor.businessName}
                      sx={{
                        minWidth: { xs: '280px', sm: '300px' },
                        maxWidth: { xs: '280px', sm: '300px' },
                        flexShrink: 0,
                        background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                        borderRadius: 4,
                        border: '1px solid rgba(0, 132, 255, 0.15)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isVisible ? 'scale(1)' : 'scale(0.95)',
                        opacity: isVisible ? 1 : 0.7,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: 'linear-gradient(90deg, #0084FF 0%, #00B4FF 100%)',
                          transform: 'scaleX(0)',
                          transformOrigin: 'left',
                          transition: 'transform 0.6s ease',
                        },
                        '&:hover': {
                          transform: 'translateY(-8px) scale(1.02)',
                          boxShadow: '0 12px 40px rgba(154, 129, 116, 0.25)',
                          borderColor: '#0084FF',
                          '&::before': {
                            transform: 'scaleX(1)',
                          },
                        },
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                          <Avatar
                            sx={{
                              width: 64,
                              height: 64,
                              background: 'linear-gradient(135deg, #0084FF 0%, #00B4FF 100%)',
                              fontSize: 28,
                              fontWeight: 700,
                              boxShadow: '0 4px 12px rgba(0, 132, 255, 0.3)',
                              transition: 'transform 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.1) rotate(5deg)',
                              },
                            }}
                          >
                            {vendor.businessName?.charAt(0) || <Store />}
                          </Avatar>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: '#050206',
                                fontSize: '1.15rem',
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {vendor.businessName || 'Vendor Name'}
                            </Typography>
                            <Box
                              sx={{
                                display: 'inline-block',
                                px: 1.5,
                                py: 0.3,
                                borderRadius: 2,
                                backgroundColor: 'rgba(0, 132, 255, 0.1)',
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#0084FF',
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                }}
                              >
                                {vendor.category || 'Category'}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {vendor.rating?.average && (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1, 
                              mt: 2.5,
                              p: 1.5,
                              borderRadius: 2,
                              backgroundColor: 'rgba(0, 132, 255, 0.05)',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  sx={{
                                    color: i < Math.floor(vendor.rating.average) 
                                      ? '#0084FF' 
                                      : 'rgba(0, 132, 255, 0.2)',
                                    fontSize: 16,
                                  }}
                                />
                              ))}
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: '#050206',
                                ml: 0.5,
                              }}
                            >
                              {vendor.rating.average.toFixed(1)}
                            </Typography>
                            {vendor.rating.count && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#0084FF',
                                  ml: 0.5,
                                }}
                              >
                                ({vendor.rating.count} reviews)
                              </Typography>
                            )}
                          </Box>
                        )}

                        {vendor.location?.city && (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1, 
                              mt: 2,
                              pt: 2,
                              borderTop: '1px solid rgba(0, 132, 255, 0.1)',
                            }}
                          >
                            <LocationOn sx={{ color: '#0084FF', fontSize: 18 }} />
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#050206',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                              }}
                            >
                              {vendor.location.city}
                              {vendor.location.state && `, ${vendor.location.state}`}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Box>

            {/* Right Arrow with Gradient */}
            <IconButton
              onClick={() => scrollVendors('right')}
              disabled={vendorScrollX >= Math.max(0, (vendors.length - (isMobile ? 1 : 3)) * 316)}
              sx={{
                display: { xs: 'none', md: 'flex' },
                width: 48,
                height: 48,
                backgroundColor: 'white',
                color: '#0084FF',
                border: '2px solid #0084FF',
                boxShadow: '0 4px 12px rgba(0, 132, 255, 0.2)',
                zIndex: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#0084FF',
                  color: 'white',
                  transform: 'scale(1.1)',
                  boxShadow: '0 6px 20px rgba(0, 132, 255, 0.4)',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(0, 132, 255, 0.1)',
                  borderColor: 'rgba(0, 132, 255, 0.3)',
                  color: 'rgba(0, 132, 255, 0.3)',
                },
              }}
            >
              →
            </IconButton>
          </Box>

          {/* Progress Indicators */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1.5,
              mt: { xs: 4, md: 6 },
            }}
          >
            {vendors.map((_, index) => {
              const cardWidth = isMobile ? 280 : 300;
              const gap = isMobile ? 16 : 24;
              const scrollAmount = cardWidth + gap;
              const currentPage = Math.round(vendorScrollX / scrollAmount);
              const isActive = currentPage === index;
              
              return (
                <Box
                  key={index}
                  onClick={() => {
                    setVendorScrollX(index * scrollAmount);
                  }}
                  sx={{
                    width: isActive ? 32 : 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: isActive ? '#0084FF' : 'rgba(0, 132, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: isActive ? '#0066CC' : 'rgba(0, 132, 255, 0.5)',
                      width: isActive ? 32 : 16,
                    },
                  }}
                />
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* Discover Hot Deals Near You Section - Mobile Phone Design */}
      <Box
        sx={{
          backgroundColor: 'white',
          py: { xs: 8, md: 12 },
          px: { xs: 2, sm: 4, md: 6 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: 'center',
              gap: { xs: 6, md: 8 },
            }}
          >
            {/* Left Content */}
            <Box
              sx={{
                flex: { xs: '1 1 100%', lg: '1 1 45%' },
                textAlign: { xs: 'center', lg: 'left' },
                animation: 'fadeInLeft 0.8s ease-out',
                '@keyframes fadeInLeft': {
                  '0%': { opacity: 0, transform: 'translateX(-40px)' },
                  '100%': { opacity: 1, transform: 'translateX(0)' },
                },
              }}
            >
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                component="h2"
                sx={{
                  fontWeight: 700,
                  color: '#050206',
                  mb: 3,
                  fontSize: { xs: '2rem', md: '3rem' },
                  animation: 'fadeInUp 0.8s ease-out 0.2s both',
                }}
              >
                Discover hot deals near you.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#050206',
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  lineHeight: 1.7,
                  maxWidth: { xs: '100%', md: '90%' },
                  animation: 'fadeInUp 0.8s ease-out 0.4s both',
                }}
              >
                Explore amazing deals across Food, Fashion, Electronics, Health & Beauty, Jewelry, Kids & Baby, Grocery, Technology, Sports & Fitness, Wellness, Home & Garden, Services, Wedding & Events, and Outdoor & Adventure. Use smart filters to find deals by category, distance, or price so you always get the best offers.
              </Typography>
            </Box>
          
            {/* Right - Mobile Phone Mockup with Map */}
            <Box
              sx={{
                flex: { xs: '1 1 100%', lg: '1 1 55%' },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                animation: 'fadeInRight 0.8s ease-out 0.3s both',
              }}
            >
              {/* Phone Frame */}
              <Box
                sx={{
                  width: '100%',
                  maxWidth: { xs: '280px', sm: '320px', md: '350px' },
                  height: { xs: '600px', sm: '680px', md: '740px' },
                  backgroundColor: '#1a1a1a',
                  borderRadius: { xs: 4, md: 6 },
                  border: '8px solid #0a0a0a',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  animation: 'phoneSlideIn 1s ease-out 0.5s both, phoneFloat2 4s ease-in-out infinite 1.5s',
                  '@keyframes phoneSlideIn': {
                    '0%': { opacity: 0, transform: 'translateX(60px) scale(0.9)' },
                    '100%': { opacity: 1, transform: 'translateX(0) scale(1)' },
                  },
                  '@keyframes phoneFloat2': {
                    '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                    '50%': { transform: 'translateY(-12px) rotate(-1deg)' },
                  },
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.03)',
                    boxShadow: '0 30px 80px rgba(0, 132, 255, 0.3)',
                  },
                }}
              >
                {/* Phone Notch */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '120px',
                    height: '20px',
                    backgroundColor: '#0a0a0a',
                    borderRadius: '0 0 12px 12px',
                    zIndex: 10,
                  }}
                />

                {/* Phone Screen Content */}
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#2a2a2a',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      backgroundColor: '#2a2a2a',
                      px: 2,
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      borderBottom: '1px solid rgba(154, 129, 116, 0.2)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        background: 'linear-gradient(135deg, #0084FF 0%, #00B4FF 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Store sx={{ fontSize: 18, color: 'white' }} />
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color: 'white',
                        fontSize: '1rem',
                        flexGrow: 1,
                      }}
                    >
                      DiscountDays
                    </Typography>
                    <LocationOn sx={{ color: 'white', fontSize: 20 }} />
                  </Box>

                  {/* Search Bar */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      backgroundColor: '#2a2a2a',
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: '#1a1a1a',
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        border: '1px solid rgba(0, 132, 255, 0.2)',
                        animation: 'slideDown 0.6s ease-out 1s both',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: '#0084FF',
                          backgroundColor: '#252525',
                        },
                      }}
                    >
                      <Store sx={{ color: '#0084FF', fontSize: 18 }} />
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.875rem',
                          flexGrow: 1,
                        }}
                      >
                        Search deals...
                      </Typography>
                    </Box>
                  </Box>

                  {/* Category Filters */}
                  <Box
                    sx={{
                      px: 2,
                      pb: 1.5,
                      display: 'flex',
                      gap: 1,
                      overflowX: 'auto',
                      '&::-webkit-scrollbar': { display: 'none' },
                      scrollbarWidth: 'none',
                    }}
                  >
                    {['Food', 'Fashion', 'Tech'].map((cat, i) => (
                      <Box
                        key={cat}
                        sx={{
                          backgroundColor: i === 0 ? '#0084FF' : '#1a1a1a',
                          color: i === 0 ? 'white' : 'rgba(255, 255, 255, 0.7)',
                          borderRadius: 2,
                          px: 2,
                          py: 0.8,
                          whiteSpace: 'nowrap',
                          animation: `slideInLeft 0.5s ease-out ${1.1 + i * 0.1}s both`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.1) translateY(-2px)',
                            backgroundColor: '#0084FF',
                            color: 'white',
                          },
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          border: i === 0 ? 'none' : '1px solid rgba(154, 129, 116, 0.2)',
                          cursor: 'pointer',
                        }}
                      >
                        {cat}
                      </Box>
                    ))}
                  </Box>

                  {/* Map Area */}
                  <Box
                    sx={{
                      flex: 1,
                      backgroundColor: '#4a7c59',
                      position: 'relative',
                      overflow: 'hidden',
                      minHeight: 0,
                    }}
                  >
                    {/* Map Markers */}
                    {[
                      { top: '25%', left: '45%' },
                      { top: '40%', left: '65%' },
                      { top: '55%', left: '35%' },
                      { top: '45%', left: '20%' },
                    ].map((pos, i) => (
                      <Box
                        key={i}
                        sx={{
                          position: 'absolute',
                          top: pos.top,
                          left: pos.left,
                          transform: 'translate(-50%, -50%)',
                          animation: `markerPulse 0.8s ease-out ${1.4 + i * 0.2}s both, markerBounce 2s ease-in-out infinite ${2 + i * 0.3}s`,
                          '@keyframes markerPulse': {
                            '0%': { opacity: 0, transform: 'translate(-50%, -50%) scale(0)' },
                            '50%': { transform: 'translate(-50%, -50%) scale(1.2)' },
                            '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
                          },
                          '@keyframes markerBounce': {
                            '0%, 100%': { transform: 'translate(-50%, -50%) translateY(0)' },
                            '50%': { transform: 'translate(-50%, -50%) translateY(-8px)' },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: '#0084FF',
                            border: '2px solid white',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.3)',
                              boxShadow: '0 4px 12px rgba(0, 132, 255, 0.6)',
                            },
                          }}
                        >
                          <Store sx={{ color: 'white', fontSize: 10 }} />
                        </Box>
                      </Box>
                    ))}

                    {/* Center Location Pin */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        animation: 'pinPulse 1s ease-out 1.2s both, pinPulseLoop 2s ease-in-out infinite 2.2s',
                        '@keyframes pinPulse': {
                          '0%': { opacity: 0, transform: 'translate(-50%, -50%) scale(0)' },
                          '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
                        },
                        '@keyframes pinPulseLoop': {
                          '0%, 100%': { transform: 'translate(-50%, -50%) scale(1)' },
                          '50%': { transform: 'translate(-50%, -50%) scale(1.1)' },
                        },
                      }}
                    >
                      <LocationOn sx={{ color: '#0084FF', fontSize: 32 }} />
                    </Box>
                  </Box>

                  {/* Deal Cards List */}
                  <Box
                    sx={{
                      backgroundColor: '#2a2a2a',
                      maxHeight: '180px',
                      overflow: 'auto',
                      '&::-webkit-scrollbar': { width: '4px' },
                      '&::-webkit-scrollbar-track': { backgroundColor: '#1a1a1a' },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: '#0084FF', borderRadius: '2px' },
                    }}
                  >
                    {[
                      { title: 'Fashion Sale', rating: 4.5, distance: '2.5 km', discount: '50% OFF', icon: <ShoppingBag /> },
                      { title: 'Electronics', rating: 4.5, distance: '2.5 km', discount: '30% OFF', icon: <TrendingDown /> },
                      { title: 'Food Deal', rating: 4.5, distance: '2.5 km', discount: '25% OFF', icon: <LocalOffer /> },
                    ].map((deal, i) => (
                      <Box
                        key={i}
                        sx={{
                          backgroundColor: '#1a1a1a',
                          borderRadius: 2,
                          p: 1.5,
                          mb: 1,
                          mx: 1.5,
                          mt: i === 0 ? 1.5 : 0,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          border: '1px solid rgba(0, 132, 255, 0.1)',
                          animation: `cardSlideUp 0.5s ease-out ${1.5 + i * 0.1}s both`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateX(5px)',
                            backgroundColor: '#252525',
                            borderColor: '#0084FF',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            backgroundColor: '#0084FF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Box sx={{ color: 'white', display: 'flex' }}>{deal.icon}</Box>
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: 'white',
                              fontSize: '0.875rem',
                              mb: 0.3,
                            }}
                          >
                            {deal.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Star sx={{ color: '#0084FF', fontSize: 12 }} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255, 255, 255, 0.6)',
                                fontSize: '0.75rem',
                              }}
                            >
                              {deal.rating}
                            </Typography>
                            <Box
                              component="span"
                              sx={{
                                color: 'rgba(255, 255, 255, 0.4)',
                                mx: 0.3,
                              }}
                            >
                              •
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255, 255, 255, 0.6)',
                                fontSize: '0.75rem',
                              }}
                            >
                              {deal.distance}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#0084FF',
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            flexShrink: 0,
                          }}
                        >
                          {deal.discount}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Bottom Navigation */}
                  <Box
                    sx={{
                      backgroundColor: '#2a2a2a',
                      borderTop: '1px solid rgba(0, 132, 255, 0.2)',
                      px: 3,
                      py: 1.5,
                      display: 'flex',
                      justifyContent: 'space-around',
                      alignItems: 'center',
                    }}
                  >
                    <Store sx={{ color: '#0084FF', fontSize: 24 }} />
                    <Favorite sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: 24 }} />
                    <LocationOn sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: 24 }} />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CTA Section before Footer */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #0084FF 0%, #00B4FF 100%)',
          py: { xs: 8, md: 12 },
          px: { xs: 2, sm: 4 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
          }}
        >
          <Particles
            particleColors={['#ffffff', '#ffffff']}
            particleCount={200}
            particleSpread={10}
            speed={0.1}
            particleBaseSize={100}
            moveParticlesOnHover={true}
            alphaParticles={false}
            disableRotation={false}
          />
        </Box>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box
            sx={{
              textAlign: 'center',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Typography
              variant={isMobile ? 'h3' : 'h2'}
              component="h2"
              sx={{
                fontWeight: 700,
                color: 'white',
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
                animation: 'fadeInUp 0.8s ease-out',
              }}
            >
              Join Thousands of Smart Shoppers
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.8,
                mb: 1,
                maxWidth: '600px',
                mx: 'auto',
                animation: 'fadeInUp 0.8s ease-out 0.2s both',
              }}
            >
              Discover amazing deals across all categories. Download the app today and start saving on everything you love.
            </Typography>
            
            {/* User App Downloads */}
            <Box sx={{ mb: 4, mt: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.875rem',
                  mb: 2,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Discount Days App
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                justifyContent: 'center',
                  maxWidth: 450,
                mx: 'auto',
              }}
            >
              <Button
                  component="a"
                  href="https://apps.apple.com/in/app/discount-days-app/id6756425497"
                  target="_blank"
                  rel="noopener noreferrer"
                variant="contained"
                startIcon={<Apple />}
                sx={{
                  backgroundColor: 'white',
                  color: '#050206',
                  textTransform: 'none',
                  fontWeight: 600,
                    px: 4,
                    py: 1.75,
                    borderRadius: 3,
                    fontSize: '1rem',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                    animation: 'fadeInUp 0.8s ease-out 0.4s both',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    textDecoration: 'none',
                  '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      transform: 'translateY(-3px) scale(1.05)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                  },
                }}
              >
                App Store
              </Button>
              <Button
                  component="a"
                  href="https://play.google.com/store/apps/details?id=com.hotdeals.app"
                  target="_blank"
                  rel="noopener noreferrer"
                variant="outlined"
                sx={{
                  borderColor: 'white',
                    borderWidth: 2,
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                    px: 4,
                    py: 1.75,
                    borderRadius: 3,
                    fontSize: '1rem',
                    animation: 'fadeInUp 0.8s ease-out 0.5s both',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    textDecoration: 'none',
                  '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      transform: 'translateY(-3px) scale(1.05)',
                      boxShadow: '0 8px 24px rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                Google Play
              </Button>
              </Box>
            </Box>

            {/* Vendor App Downloads */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.875rem',
                  mb: 2,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Vendor App (For Business Owners)
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: 'center',
                  maxWidth: 450,
                  mx: 'auto',
                }}
              >
                <Button
                  component="a"
                  href="https://apps.apple.com/in/app/discount-days-vendor/id6756434067"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  startIcon={<Store />}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                    borderWidth: 2,
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    py: 1.75,
                    borderRadius: 3,
                    fontSize: '1rem',
                    animation: 'fadeInUp 0.8s ease-out 0.6s both',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    textDecoration: 'none',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      transform: 'translateY(-3px) scale(1.05)',
                      boxShadow: '0 8px 24px rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  App Store
                </Button>
                <Button
                  component="a"
                  href="https://play.google.com/store/apps/details?id=com.discountdays.vendorapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                    borderWidth: 2,
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    py: 1.75,
                    borderRadius: 3,
                    fontSize: '1rem',
                    animation: 'fadeInUp 0.8s ease-out 0.7s both',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    textDecoration: 'none',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      transform: 'translateY(-3px) scale(1.05)',
                      boxShadow: '0 8px 24px rgba(255, 255, 255, 0.2)',
                    },
                  }}
                >
                  Google Play
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          backgroundColor: '#fafbfc',
          borderTop: '1px solid rgba(0, 132, 255, 0.1)',
          color: '#050206',
          py: { xs: 6, md: 8 },
          px: { xs: 2, sm: 4 },
        }}
      >
        <Container maxWidth="lg">
        <Box
          sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              gap: { xs: 4, md: 6 },
              mb: 4,
            }}
          >
            {/* Left - Logo and Copyright */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 40%' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          component="img"
          src="/logo.png"
          alt="DiscountDays Logo"
          sx={{
                    width: 40,
                    height: 40,
                    objectFit: 'contain',
                    mr: 1.5,
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0084FF' }}>
                 DiscountDays
               </Typography>
              </Box>
                <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                © copyright DiscountDays 2025. All rights reserved.
               </Typography>
             </Box>

            {/* Right - Footer Links */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 3, sm: 6 },
                flex: { xs: '1 1 100%', md: '1 1 60%' },
              }}
            >
              {/* Socials */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#0084FF' }}>
                  Socials
               </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
               <Typography 
                 variant="body2" 
                    sx={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      cursor: 'pointer',
                      '&:hover': { color: '#0084FF' },
                    }}
                  >
                    Instagram
               </Typography>
               <Typography 
                 variant="body2" 
                    sx={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      cursor: 'pointer',
                      '&:hover': { color: '#0084FF' },
                    }}
                  >
                    LinkedIn
               </Typography>
                </Box>
              </Box>

              {/* Legal */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#0084FF' }}>
                  Legal
               </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
               <Typography 
                 variant="body2" 
                    sx={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      '&:hover': { color: '#0084FF' },
                    }}
                    onClick={() => navigate('/privacy-policy')}
                  >
                    Privacy Policy
               </Typography>
               <Typography 
                 variant="body2" 
                    sx={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      '&:hover': { color: '#0084FF' },
                    }}
                    onClick={() => navigate('/delete-account')}
                  >
                    Delete Account
               </Typography>
             </Box>
              </Box>

              {/* Become a Partner */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#0084FF' }}>
                  Become a Partner
               </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      cursor: 'pointer',
                      '&:hover': { color: '#0084FF' },
                    }}
                  >
                    Sign Up
               </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(0, 0, 0, 0.6)',
                      cursor: 'pointer',
                      '&:hover': { color: '#0084FF' },
                    }}
                    onClick={() => navigate('/login')}
                  >
                    Login
               </Typography>
             </Box>
                       </Box>
            </Box>
           </Box>
        </Container>
        </Box>
    </Box>
  );
};

export default HomePage;